/**
 * All possible shareable resource types.
 */
export enum ShareableType {
  COMPETITOR_ANALYSIS = 'competitor-analysis',
  POTENTIAL_ANALYSIS = 'potential-analysis',
  MARKET_PROJECTION = 'market-projection',
  BUSINESS_CASE = 'business-case',
  AT_CUBE_DASHBOARD = 'at-cube-dashboard',
  AT_VIRTUAL_CLINIC = 'at-virtual-clinic',
  CH_CUBE_DASHBOARD = 'ch-cube-dashboard',
  CH_VIRTUAL_CLINIC = 'ch-virtual-clinic',
  VIRTUAL_CLINIC = 'virtual-clinic',
  MARKET = 'market',
  CASE_EXPORT_TEMPLATE = 'case-export-template',
  // Wire string must match the backend user_data_graph ShareableType,
  // otherwise the central /shareables/ list can't resolve saved templates.
  // BDA-5744: the backend resolver was normalized to kebab-case
  // 'clinics-export-template' (previously 'qb_export_template').
  CLINICS_EXPORT_TEMPLATE = 'clinics-export-template',
  CODE_SPECTRUM = 'code-spectrum',
  CLINICS_FILTER = 'clinics-filter',
  CLINIC_OPERATING_NUMBER_GROUP = 'clinic-operating-number-group',
  PROFITABILITY_ANALYSIS_FORM = 'profitability-analysis-form',
  VIRTUAL_CLINIC_ANALYSIS_DASHBOARD = 'virtual-clinic-analysis-dashboard',
  VIRTUAL_CLINIC_REFERRAL_IMPORT = 'virtual-clinic-referral-import',
  VIRTUAL_CLINIC_BENCHMARKING_GROUP = 'virtual-clinic-benchmarking-group',
  SCENARIO_ANALYSIS = 'scenario',
  SCENARIO_ANALYSIS_OPEN_CLOSE = 'demand-analysis-open-close-scenario',
  SCENARIO_ANALYSIS_OPEN_CLOSE_INEK_DATASOURCE = 'bmg-open-close-scenario',
  SCENARIO_VV_ANALYSIS_OPEN_CLOSE_INEK_DATASOURCE = 'bmg-vorhalteverguetung-calculation',
  SCENARIO_VV_ANALYSIS_OPEN_CLOSE = 'demand-analysis-vorhalteverguetung-calculation',
  UK_CODE_SPECTRUM = 'uk-code-spectrum',
  VIRTUAL_CLINIC_CZ = 'virtual-clinic-cz',
  // Klia shareable types as stored and returned by the backend; existing rows
  // were migrated from the legacy `klio-` spelling (meta-backend PR #1269).
  KLIA_CONVERSATION_THREAD = 'klia-conversation-thread',
  KLIA_ARTIFACT = 'klia-artifact',
  KLIA_ATTACHMENT = 'klia-attachment',
  FILE = 'file',
  FOLDER = 'folder',
  // BDA-5943 shipped: user-data-graph registers this shareTargetType and
  // resolves the dashboard's dependencies from
  // `kennzahlen_benchmarking.resource_mapping`
  // (`KennzahlenBenchmarkingDashboardDependencyResolver`), so `share-preview`
  // answers for it — a dashboard's tiles contribute their `clinicFilterId`
  // cohort nodes and their map layers' saved filters/markets as children.
  KENNZAHLEN_BENCHMARKING_DASHBOARD = 'kennzahlen-benchmarking-dashboard',
}

export const SHAREABLE_TYPE_DISPLAY_NAME: { [name in ShareableType]: string } = {
  [ShareableType.CH_VIRTUAL_CLINIC]: 'Virtuelle Klinik CH',
  [ShareableType.AT_CUBE_DASHBOARD]: 'Cube Dashboard AT',
  [ShareableType.AT_VIRTUAL_CLINIC]: 'Virtuelle Klinik AT',
  [ShareableType.CH_CUBE_DASHBOARD]: 'Cube Dashboard CH',
  [ShareableType.COMPETITOR_ANALYSIS]: 'Wettbewerbsanalyse',
  [ShareableType.POTENTIAL_ANALYSIS]: 'Potentialanalyse',
  [ShareableType.MARKET_PROJECTION]: 'Marktprognose',
  [ShareableType.BUSINESS_CASE]: 'Business Case',
  [ShareableType.CASE_EXPORT_TEMPLATE]: 'Fall Export Vorlage',
  [ShareableType.CLINICS_EXPORT_TEMPLATE]: 'Klinik-Filter Export Vorlage',
  [ShareableType.CLINIC_OPERATING_NUMBER_GROUP]: 'Kennzahlengruppe',
  [ShareableType.CODE_SPECTRUM]: 'Ziffer-Spektra',
  [ShareableType.CLINICS_FILTER]: 'Klinik-Filter',
  [ShareableType.MARKET]: 'Markt',
  [ShareableType.VIRTUAL_CLINIC]: 'Virtuelle Klinik',
  [ShareableType.VIRTUAL_CLINIC_ANALYSIS_DASHBOARD]: 'Cube Dashboard',
  [ShareableType.VIRTUAL_CLINIC_BENCHMARKING_GROUP]:
    'Virtuelle Benchmarking-Gruppe',
  [ShareableType.VIRTUAL_CLINIC_REFERRAL_IMPORT]: 'Einweiser Import',
  [ShareableType.SCENARIO_ANALYSIS]: 'Szenario-Analyse',
  [ShareableType.SCENARIO_ANALYSIS_OPEN_CLOSE]:
    'Versorgunganalyse - Simulation',
  [ShareableType.SCENARIO_ANALYSIS_OPEN_CLOSE_INEK_DATASOURCE]:
    'Versorgunganalyse - Simulation',
  [ShareableType.SCENARIO_VV_ANALYSIS_OPEN_CLOSE_INEK_DATASOURCE]:
    'Versorgunganalyse - Kalkulation',
  [ShareableType.SCENARIO_VV_ANALYSIS_OPEN_CLOSE]:
    'Versorgunganalyse - Kalkulation',
  [ShareableType.UK_CODE_SPECTRUM]: 'Code-Spectra UK',
  [ShareableType.VIRTUAL_CLINIC_CZ]: 'Virtuelle Klinik',
  [ShareableType.KLIA_CONVERSATION_THREAD]: 'Klia Konversation',
  [ShareableType.KLIA_ARTIFACT]: 'Klia Artefakt',
  [ShareableType.KLIA_ATTACHMENT]: 'Klia Anhang',
  [ShareableType.FILE]: 'Datei',
  [ShareableType.PROFITABILITY_ANALYSIS_FORM]: 'Wirtschaftlichkeits-Analyse',
  [ShareableType.FOLDER]: 'Ordner',
  [ShareableType.KENNZAHLEN_BENCHMARKING_DASHBOARD]:
    'Kennzahlen-Benchmarking Dashboard',
};
